// NLE Theme JS — Bootstrap + Scroll Reveal + Sticky Header

/**
 * NLE Theme JavaScript
 *
 * Includes Bootstrap 5 and custom functionality.
 * Institutional, refined executive search aesthetic.
 */

// Import Bootstrap
import 'bootstrap';

/**
 * Skip Link Focus Fix
 * Helps with accessibility for keyboard only users.
 */
( function() {
	const isIe = /(trident|msie)/i.test( navigator.userAgent );

	if ( isIe && document.getElementById && window.addEventListener ) {
		window.addEventListener(
			'hashchange',
			function() {
				const id = location.hash.substring( 1 );
				let element;

				if ( ! /^[A-z0-9_-]+$/.test( id ) ) {
					return;
				}

				element = document.getElementById( id );

				if ( element ) {
					if ( ! /^(?:a|select|input|button|textarea)$/i.test( element.tagName ) ) {
						element.tabIndex = -1;
					}

					element.focus();
				}
			},
			false
		);
	}
} )();

/**
 * Scroll Reveal
 * Uses IntersectionObserver to reveal elements as they enter the viewport.
 * Adds .revealed class to elements with .reveal-on-scroll
 */
( function() {
	'use strict';

	function init() {
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		if ( prefersReducedMotion ) {
			document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
				el.classList.add( 'revealed' );
			} );
			return;
		}

		if ( ! ( 'IntersectionObserver' in window ) ) {
			document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
				el.classList.add( 'revealed' );
			} );
			return;
		}

		const observerOptions = {
			root: null,
			rootMargin: '0px 0px -50px 0px',
			threshold: 0.1
		};

		const observer = new IntersectionObserver( function( entries ) {
			entries.forEach( function( entry ) {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'revealed' );
					observer.unobserve( entry.target );
				}
			} );
		}, observerOptions );

		document.querySelectorAll( '.reveal-on-scroll' ).forEach( function( el ) {
			observer.observe( el );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * NLE Reveal System
 * Uses IntersectionObserver to reveal elements with .nle-reveal class.
 * Adds .is-visible when element enters viewport at 15% threshold.
 */
( function() {
	'use strict';

	function init() {
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		const reveals = document.querySelectorAll( '.nle-reveal' );

		if ( ! reveals.length ) {
			return;
		}

		if ( prefersReducedMotion || ! ( 'IntersectionObserver' in window ) ) {
			reveals.forEach( function( el ) {
				el.classList.add( 'is-visible' );
			} );
			return;
		}

		const observer = new IntersectionObserver( function( entries ) {
			entries.forEach( function( entry ) {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'is-visible' );
					observer.unobserve( entry.target );
				}
			} );
		}, { threshold: 0.15 } );

		reveals.forEach( function( el ) {
			observer.observe( el );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Smooth Scroll
 * Handles smooth scrolling for anchor links with header offset.
 */
( function() {
	'use strict';

	function init() {
		document.querySelectorAll( 'a[href^="#"]' ).forEach( function( anchor ) {
			anchor.addEventListener( 'click', function( e ) {
				const targetId = this.getAttribute( 'href' );

				if ( targetId === '#' ) {
					return;
				}

				const target = document.querySelector( targetId );

				if ( target ) {
					e.preventDefault();
					const headerOffset = 80;
					const elementPosition = target.getBoundingClientRect().top;
					const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

					window.scrollTo( {
						top: offsetPosition,
						behavior: 'smooth'
					} );
				}
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Smart Sticky Header + Hero Parallax
 * Single scroll listener handles both features via shared rAF loop.
 */
( function() {
	'use strict';

	var lastScrollY = 0;
	var ticking = false;
	var SCROLL_THRESHOLD = 10;

	function init() {
		var navbar = document.getElementById( 'main-nav' );

		if ( ! navbar ) {
			return;
		}

		var heroSection = document.querySelector( '.section-hero' );
		var heroBg = document.querySelector( '.section-hero__background' );
		var prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		// Inner pages without a hero — start condensed immediately
		if ( ! heroSection ) {
			navbar.classList.add( 'is-condensed' );
		}

		function getHeroBottom() {
			if ( ! heroSection ) {
				return 0;
			}
			return heroSection.offsetTop + heroSection.offsetHeight;
		}

		function update() {
			var currentScrollY = window.scrollY;
			var delta = currentScrollY - lastScrollY;
			var heroHeight = heroSection ? heroSection.offsetHeight : 0;
			var pastHero = currentScrollY > getHeroBottom();

			// Hero parallax
			if ( heroBg && ! prefersReducedMotion && currentScrollY < heroHeight ) {
				heroBg.style.transform = 'translateY(' + ( currentScrollY * 0.3 ) + 'px) scale(1.05)';
			}

			// Sticky header
			if ( Math.abs( delta ) >= SCROLL_THRESHOLD ) {
				if ( ! pastHero ) {
					navbar.classList.remove( 'is-hidden', 'is-condensed' );
				} else if ( delta > 0 ) {
					navbar.classList.add( 'is-hidden' );
					navbar.classList.remove( 'is-condensed' );
				} else {
					navbar.classList.remove( 'is-hidden' );
					navbar.classList.add( 'is-condensed' );
				}

				lastScrollY = currentScrollY;
			}

			ticking = false;
		}

		function onScroll() {
			if ( ! ticking ) {
				requestAnimationFrame( update );
				ticking = true;
			}
		}

		lastScrollY = window.scrollY;
		window.addEventListener( 'scroll', onScroll, { passive: true } );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Mobile Navigation Toggle
 * Opens/closes the full-screen mobile nav overlay.
 */
( function() {
	'use strict';

	function init() {
		var toggle = document.querySelector( '.nle-navbar__toggle' );
		var mobileNav = document.getElementById( 'nle-mobile-nav' );

		if ( ! toggle || ! mobileNav ) {
			return;
		}

		toggle.addEventListener( 'click', function() {
			var isOpen = mobileNav.classList.contains( 'is-open' );

			if ( isOpen ) {
				mobileNav.classList.remove( 'is-open' );
				toggle.setAttribute( 'aria-expanded', 'false' );
				mobileNav.setAttribute( 'aria-hidden', 'true' );
				document.body.style.overflow = '';
			} else {
				mobileNav.classList.add( 'is-open' );
				toggle.setAttribute( 'aria-expanded', 'true' );
				mobileNav.setAttribute( 'aria-hidden', 'false' );
				document.body.style.overflow = 'hidden';
			}
		} );

		// Close on link click
		var navLinks = mobileNav.querySelectorAll( 'a' );
		navLinks.forEach( function( link ) {
			link.addEventListener( 'click', function() {
				mobileNav.classList.remove( 'is-open' );
				toggle.setAttribute( 'aria-expanded', 'false' );
				mobileNav.setAttribute( 'aria-hidden', 'true' );
				document.body.style.overflow = '';
			} );
		} );

		// Close on escape key
		document.addEventListener( 'keydown', function( e ) {
			if ( e.key === 'Escape' && mobileNav.classList.contains( 'is-open' ) ) {
				mobileNav.classList.remove( 'is-open' );
				toggle.setAttribute( 'aria-expanded', 'false' );
				mobileNav.setAttribute( 'aria-hidden', 'true' );
				document.body.style.overflow = '';
				toggle.focus();
			}
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();

/**
 * Back to Top Button
 * Shows a fixed button after scrolling 2x viewport height.
 */
( function() {
	'use strict';

	function init() {
		var backToTop = document.querySelector( '.nle-back-to-top' );

		if ( ! backToTop ) {
			return;
		}

		var threshold = window.innerHeight * 2;
		var prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

		window.addEventListener( 'scroll', function() {
			if ( window.scrollY > threshold ) {
				backToTop.classList.add( 'is-visible' );
			} else {
				backToTop.classList.remove( 'is-visible' );
			}
		}, { passive: true } );

		backToTop.addEventListener( 'click', function() {
			window.scrollTo( {
				top: 0,
				behavior: prefersReducedMotion ? 'auto' : 'smooth'
			} );
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
